import React, { useEffect, useState, useRef } from "react";
import api, { API } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { PanelHead, Badge, Empty, Field, Btn, useFocusRecord, focusCls } from "./common";
import { Plus, Trash2, FileText, Download, Eye } from "lucide-react";
import { toast } from "sonner";

const statusColors = { draft: "gray", sent: "amber", partial: "blue", accepted: "green", started: "purple" };
const emptyItem = () => ({ description: "", quantity: 1, unit: "adet", unit_price: 0, line_total: 0 });

/* Uninvoiced completed work + overdue/partial invoices, derived ONLY from real due dates and payment records.
   Hidden when there is nothing to follow up. Reminders are drafts (sent only after the e-mail confirm step). */
const FollowupsCard = () => {
  const { tx } = useI18n();
  const [f, setF] = useState(null);
  useEffect(() => { api.get("/finance/followups").then((r) => setF(r.data)).catch(() => {}); }, []);
  if (!f || (!f.overdue.length && !f.uninvoiced.length && !f.missing_due_date.length)) return null;
  const draft = async (id) => {
    try { await api.post("/finance/reminder-draft", { invoice_id: id }); toast.success(tx("Hatırlatma taslağı hazır — E-postalar'da onaylayın")); }
    catch (e) { toast.error(tx(e?.response?.data?.detail || "İşlem başarısız")); }
  };
  return (
    <div className="panel p-3 mb-4 text-xs space-y-1.5" data-testid="finance-followups">
      {f.overdue.map((r) => (
        <div key={r.proposal_id} className="flex items-center justify-between gap-2">
          <span className="text-amber-200">{r.number} · {r.client || "—"} · £{r.outstanding.toFixed(2)} · {r.days_overdue} {tx("gün gecikmiş")}{r.state === "partial" ? ` · ${tx("kısmi ödendi")}` : ""}</span>
          <Btn variant="ghost" onClick={() => draft(r.proposal_id)}>{tx("Hatırlatma taslağı")}</Btn>
        </div>
      ))}
      {f.missing_due_date.map((r) => (
        <div key={r.proposal_id} className="text-white/55">{r.number} · £{r.outstanding.toFixed(2)} {tx("ödenmemiş · vade tarihi kayıtlı değil")}</div>
      ))}
      {f.uninvoiced.map((u) => (
        <div key={u.project_id} className="text-cyan-200/80">{u.name}: {tx("faturalanmamış tamamlanmış iş")} ({u.completed_tasks.length})</div>
      ))}
    </div>
  );
};

export const ProposalsPanel = ({ onChanged, focusId }) => {
  const { t, tx } = useI18n();
  const [items, setItems] = useState([]);
  useFocusRecord(items, focusId);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null);
  const [totals, setTotals] = useState(null);
  const [computing, setComputing] = useState(false);
  const [computeError, setComputeError] = useState(false);
  const [photoFiles, setPhotoFiles] = useState([]);
  const computeSeq = useRef(0);

  const load = async () => {
    const [p, c, pr] = await Promise.all([api.get("/proposals"), api.get("/clients"), api.get("/projects")]);
    setItems(p.data); setClients(c.data); setProjects(pr.data);
  };
  useEffect(() => { load(); }, []);
  const loadPhotoFiles = async () => {
    try {
      const r = await api.get("/files");
      setPhotoFiles(r.data.filter((f) => f.kind === "photo" || (f.content_type || "").startsWith("image/")));
    } catch { setPhotoFiles([]); }
  };
  const attachPhoto = async (f, type) => {
    if ((editing.photos || []).some((p) => p.file_id === f.file_id)) return;
    try {
      const r = await api.get(`/files/${f.file_id}/data`);
      const data = r.data.data.startsWith("data:") ? r.data.data : `data:${r.data.content_type || "image/jpeg"};base64,${r.data.data}`;
      setEditing((e) => ({ ...e, photos: [...(e.photos || []), { file_id: f.file_id, data, type, caption: f.caption || f.filename || "" }] }));
    } catch { toast.error(tx("Görsel eklenemedi")); }
  };
  const removePhoto = (fid) => setEditing((e) => ({ ...e, photos: (e.photos || []).filter((p) => p.file_id !== fid) }));

  const newDoc = () => setEditing({
    doc_type: "proposal", title: "", subtitle: "", scope_summary: "", client_id: "", project_id: "",
    client: {}, project: {}, line_items: [emptyItem()], discount: 0, vat_mode: "none", vat_rate: 0,
    validity_days: 30, assumptions: [], exclusions: [], programme: "", payment_terms: "",
    variations: "", notes: "", status: "draft",
  });

  const recompute = async (draft) => {
    const seq = ++computeSeq.current;
    setComputing(true); setComputeError(false);
    try {
      const r = await api.post("/proposals/compute", {
        line_items: draft.line_items, discount: draft.discount, vat_mode: draft.vat_mode, vat_rate: draft.vat_rate,
      });
      if (seq !== computeSeq.current) return;  // ignore stale response
      setTotals(r.data); setComputing(false);
    } catch (e) {
      if (seq !== computeSeq.current) return;
      // Don't keep showing a previous VAT state's total as if valid.
      setTotals(null); setComputeError(true); setComputing(false);
    }
  };
  useEffect(() => { if (editing) recompute(editing); /* eslint-disable-next-line */ }, [
    editing?.line_items?.map((i) => `${i.quantity}|${i.unit_price}`).join(","), editing?.discount, editing?.vat_mode, editing?.vat_rate]);

  useEffect(() => { if (editing) loadPhotoFiles(); /* eslint-disable-next-line */ }, [!!editing]);
  const setItem = (i, k, v) => {
    const li = [...editing.line_items]; li[i] = { ...li[i], [k]: v }; setEditing({ ...editing, line_items: li });
  };
  const lineTotal = (it) => (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);

  const buildPayload = () => {
    const client = clients.find((c) => c.client_id === editing.client_id);
    const project = projects.find((p) => p.project_id === editing.project_id);
    return { ...editing,
      client: client ? { name: client.name, email: client.email, address: client.address } : (editing.client || {}),
      project: project ? { name: project.name, address: project.address } : (editing.project || {}) };
  };
  const canExport = !!totals && !computing && !computeError;
  const save = async () => {
    if (!canExport) { toast.error(tx("Toplam güncel değil — hesaplama bitene kadar kaydedilemez")); return; }
    const payload = buildPayload();
    try {
      if (editing.proposal_id) await api.put(`/proposals/${editing.proposal_id}`, payload);
      else await api.post("/proposals", payload);
    } catch (e) { // server-side validation (e.g. negative amount, discount > subtotal): keep the editor open
      toast.error(e?.response?.data?.detail || tx("Kaydedilemedi")); return;
    }
    toast.success(tx("Teklif kaydedildi"));
    setEditing(null); setTotals(null); load(); onChanged?.();
  };
  const previewPdf = async () => {
    if (!canExport) { toast.error(tx("Toplam güncel değil — önce hesaplamanın bitmesini bekleyin")); return; }
    try {
      const res = await api.post("/proposals/preview-pdf", buildPayload(), { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error(tx("PDF önizleme başarısız")); }
  };
  const del = async (id) => { await api.delete(`/proposals/${id}`); load(); onChanged?.(); };
  const openPdf = (id) => window.open(`${API}/proposals/${id}/pdf`, "_blank");

  if (editing) {
    return (
      <div data-testid="panel-proposals">
        <PanelHead title={editing.proposal_id ? tx("Teklif düzenle") : tx("Yeni teklif")} sub={tx("Ondalık para hesapları backend'de yapılır")}
          right={<div className="flex gap-2">
            <Btn variant="ghost" onClick={() => { setEditing(null); setTotals(null); }}>{t("cancel")}</Btn>
            <Btn variant="ghost" onClick={previewPdf} data-testid="preview-pdf-btn" className={canExport ? "" : "opacity-40 pointer-events-none"}>{tx("PDF Önizle")}</Btn>
            <Btn onClick={save} data-testid="save-proposal-btn" className={canExport ? "" : "opacity-40 pointer-events-none"}>{t("save")}</Btn>
          </div>} />
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-5">
          <div className="panel p-5">
            <div className="grid md:grid-cols-2 gap-3 stagger">
              <Sel label={tx("Belge tipi")} value={editing.doc_type} onChange={(v) => setEditing({ ...editing, doc_type: v })} opts={[["proposal", tx("Teklif")], ["invoice", tx("Fatura")], ["report", tx("Rapor")]]} />
              <Field label={tx("Başlık")} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
              {editing.doc_type === "invoice" && (
                <Field label={tx("Vade tarihi")} type="date" value={editing.due_date || ""} onChange={(e) => setEditing({ ...editing, due_date: e.target.value })} data-testid="invoice-due-date" />
              )}
              <Sel label={tx("Müşteri")} value={editing.client_id} onChange={(v) => setEditing({ ...editing, client_id: v })} opts={[["", "—"], ...clients.map((c) => [c.client_id, c.name])]} />
              <Sel label={tx("Proje")} value={editing.project_id} onChange={(v) => setEditing({ ...editing, project_id: v })} opts={[["", "—"], ...projects.map((p) => [p.project_id, p.name])]} />
            </div>

            <div className="label-eyebrow mt-4 mb-2">{tx("İş kalemleri")}</div>
            <div className="space-y-2" data-testid="line-items">
              {editing.line_items.map((it, i) => (
                <div key={i} className="grid grid-cols-[1fr_60px_60px_80px_80px_28px] gap-2 items-center">
                  <input value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} placeholder={tx("Açıklama")}
                    className="bg-black/30 border border-cyan-500/20 rounded px-2 py-1.5 text-sm text-white" data-testid={`item-desc-${i}`} />
                  <input type="number" value={it.quantity} onChange={(e) => setItem(i, "quantity", +e.target.value)} className="bg-black/30 border border-cyan-500/20 rounded px-2 py-1.5 text-sm text-white" data-testid={`item-qty-${i}`} />
                  <input value={it.unit} onChange={(e) => setItem(i, "unit", e.target.value)} className="bg-black/30 border border-cyan-500/20 rounded px-2 py-1.5 text-sm text-white" />
                  <input type="number" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", +e.target.value)} className="bg-black/30 border border-cyan-500/20 rounded px-2 py-1.5 text-sm text-white" data-testid={`item-price-${i}`} />
                  <div className="text-right text-sm font-mono text-cyan-200">£{lineTotal(it).toFixed(2)}</div>
                  <button onClick={() => setEditing({ ...editing, line_items: editing.line_items.filter((_, j) => j !== i) })} className="text-white/40 hover:text-red-400"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
            <Btn variant="ghost" className="mt-3" data-testid="add-line-item-btn" onClick={() => setEditing({ ...editing, line_items: [...editing.line_items, emptyItem()] })}><Plus size={14} className="inline mr-1" />{tx("Kalem ekle")}</Btn>

            <div className="grid md:grid-cols-3 gap-3 mt-4">
              <Field label={tx("İndirim (£)")} type="number" value={editing.discount} onChange={(e) => setEditing({ ...editing, discount: +e.target.value })} />
              <Sel label="VAT" value={editing.vat_mode} onChange={(v) => setEditing({ ...editing, vat_mode: v })} opts={[["none", tx("KDV Yok / No VAT")], ["standard", tx("VAT %20")], ["custom", tx("Özel %")]]} />
              {editing.vat_mode === "custom" && <Field label="VAT %" type="number" value={editing.vat_rate} onChange={(e) => setEditing({ ...editing, vat_rate: +e.target.value })} />}
            </div>
            <p className="text-[0.68rem] text-white/40 mt-1">{tx("ModiHome varsayılan politikası:")} <b>No VAT</b>{tx(". VAT yalnızca açık seçimle eklenir.")}</p>

            <div className="label-eyebrow mt-5 mb-2">{tx("Teklif detayları (markalı PDF)")}</div>
            <div className="grid md:grid-cols-2 gap-3 stagger">
              <Field label={tx("Alt başlık")} value={editing.subtitle || ""} onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })} data-testid="q-subtitle" />
              <Field label={tx("Geçerlilik (gün)")} type="number" value={editing.validity_days ?? ""} onChange={(e) => setEditing({ ...editing, validity_days: e.target.value ? +e.target.value : null })} data-testid="q-validity" />
            </div>
            <label className="block mb-3"><span className="label-eyebrow block mb-1">{tx("Kısa kapsam özeti")}</span>
              <textarea value={editing.scope_summary || ""} onChange={(e) => setEditing({ ...editing, scope_summary: e.target.value })} data-testid="q-scope"
                className="w-full bg-black/30 border border-cyan-500/20 rounded-lg px-3 py-2 text-sm text-white min-h-[60px]" /></label>
            <div className="grid md:grid-cols-2 gap-3 stagger">
              <label className="block mb-3"><span className="label-eyebrow block mb-1">{tx("Varsayımlar (her satır bir madde)")}</span>
                <textarea value={(editing.assumptions || []).join("\n")} onChange={(e) => setEditing({ ...editing, assumptions: e.target.value.split("\n").filter(Boolean) })} data-testid="q-assumptions"
                  className="w-full bg-black/30 border border-cyan-500/20 rounded-lg px-3 py-2 text-sm text-white min-h-[60px]" /></label>
              <label className="block mb-3"><span className="label-eyebrow block mb-1">{tx("Hariç tutulanlar (her satır bir madde)")}</span>
                <textarea value={(editing.exclusions || []).join("\n")} onChange={(e) => setEditing({ ...editing, exclusions: e.target.value.split("\n").filter(Boolean) })} data-testid="q-exclusions"
                  className="w-full bg-black/30 border border-cyan-500/20 rounded-lg px-3 py-2 text-sm text-white min-h-[60px]" /></label>
            </div>
            <div className="grid md:grid-cols-2 gap-3 stagger">
              <Field label={tx("Programme / süre")} value={editing.programme || ""} onChange={(e) => setEditing({ ...editing, programme: e.target.value })} data-testid="q-programme" />
              <Field label={tx("Ödeme şartları")} value={editing.payment_terms || ""} onChange={(e) => setEditing({ ...editing, payment_terms: e.target.value })} data-testid="q-payment" />
            </div>
            <p className="text-[0.68rem] text-white/40">{tx("Ölçü, süre, ödeme ve teknik iddialar AI tarafından uydurulmaz; yalnızca girdiğiniz bilgiler PDF'e yazılır. Ek işler yazılı onayla ayrı fiyatlanır.")}</p>

            <div className="label-eyebrow mt-5 mb-2">{tx("Görseller (teklif PDF'ine eklenir)")}</div>
            {(editing.photos || []).length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3" data-testid="proposal-attached-photos">
                {editing.photos.map((p) => (
                  <div key={p.file_id} className="relative">
                    <img src={p.data} alt="" className="w-20 h-16 object-cover rounded border border-cyan-500/30" />
                    <span className="absolute bottom-0 left-0 right-0 text-[0.55rem] text-center bg-black/60 text-white/80">{p.type === "proposed" ? tx("Önerilen") : tx("Mevcut")}</span>
                    <button onClick={() => removePhoto(p.file_id)} className="absolute -top-1.5 -right-1.5 bg-red-500/80 text-white rounded-full w-4 h-4 text-[0.6rem] leading-none" data-testid={`remove-photo-${p.file_id}`}>×</button>
                  </div>
                ))}
              </div>
            )}
            {photoFiles.length === 0 ? (
              <p className="text-[0.68rem] text-white/40">{tx("Eklenebilir fotoğraf yok. Şantiye ekranından (kamera) veya Dosyalar'dan fotoğraf yükleyin.")}</p>
            ) : (
              <div className="space-y-1.5" data-testid="proposal-photo-picker">
                {photoFiles.slice(0, 12).map((f) => {
                  const attached = (editing.photos || []).some((p) => p.file_id === f.file_id);
                  return (
                    <div key={f.file_id} className="flex items-center justify-between gap-2 bg-black/20 rounded px-2.5 py-1.5">
                      <span className="text-xs text-white/70 truncate">{f.caption || f.filename || tx("fotoğraf")}</span>
                      {attached ? <Badge color="green">{tx("eklendi")}</Badge> : (
                        <div className="flex gap-1.5 shrink-0">
                          <button onClick={() => attachPhoto(f, "existing")} data-testid={`attach-existing-${f.file_id}`} className="text-[0.66rem] text-cyan-300 border border-cyan-400/30 rounded px-1.5 py-0.5">{tx("Mevcut")}</button>
                          <button onClick={() => attachPhoto(f, "proposed")} data-testid={`attach-proposed-${f.file_id}`} className="text-[0.66rem] text-amber-300 border border-amber-400/30 rounded px-1.5 py-0.5">{tx("Önerilen")}</button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="panel corner-bracket p-5" data-testid="proposal-totals">
            <div className="label-eyebrow mb-3">{tx("Önizleme toplamları")}</div>
            {computeError ? (
              <div className="text-sm text-red-300" data-testid="compute-error">
                {tx("⚠️ Bağlantı hatası — toplam hesaplanamadı. Eski toplam geçersiz sayıldı.")}
                <button onClick={() => recompute(editing)} data-testid="recompute-btn"
                  className="block mt-2 text-cyan-300 underline">{tx("Tekrar hesapla")}</button>
              </div>
            ) : computing ? (
              <div className="text-sm text-amber-300" data-testid="computing-indicator">{tx("Hesaplanıyor…")}</div>
            ) : totals ? (
              <div className="space-y-2 font-mono text-sm">
                <Line l={tx("Ara Toplam")} r={totals.subtotal} />
                <Line l={tx("İndirim")} r={totals.discount} />
                <Line l={totals.vat_mode === "none" ? tx("KDV Yok (No VAT)") : `VAT %${totals.vat_rate_percent}`} r={totals.vat_amount} />
                <div className="h-px bg-cyan-500/20 my-2" />
                <div className="flex justify-between text-lg text-cyan-200"><span>{tx("TOPLAM")}</span><span data-testid="proposal-total">£{totals.total.toFixed(2)}</span></div>
              </div>
            ) : <div className="text-xs text-white/50">{tx("Hesaplanıyor…")}</div>}
            <Sel label={tx("Durum")} value={editing.status} onChange={(v) => setEditing({ ...editing, status: v })}
              opts={[["draft", tx("Taslak")], ["sent", tx("Gönderildi")], ["partial", tx("Kısmen onay")], ["accepted", tx("Tam onay")], ["started", tx("İşe başla")]]} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="panel-proposals">
      <PanelHead title={t("nav_proposals")} sub={tx("Teklif · Fatura · Rapor")}
        right={<Btn data-testid="add-proposal-btn" onClick={newDoc}><Plus size={15} className="inline mr-1" />{t("add")}</Btn>} />
      <FollowupsCard />
      <div className="grid md:grid-cols-2 gap-3 stagger">
        {items.length === 0 && <Empty text={t("noData")} />}
        {items.map((x) => (
          <div key={x.proposal_id} data-rid={x.proposal_id} className={`panel p-4${focusCls(x.proposal_id, focusId)}`} data-testid="proposal-row">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2"><FileText size={15} className="text-cyan-300" /><span className="text-white font-medium">{x.number}</span></div>
                <div className="text-xs text-[color:var(--txt-2)] mt-1">{x.client?.name || "—"} · {x.title || x.doc_type}</div>
              </div>
              <Badge color={statusColors[x.status] || "gray"}>{x.status}</Badge>
            </div>
            <div className="flex items-center justify-between mt-3">
              <span className="font-mono text-cyan-200">£{Number(x.total || 0).toFixed(2)} <span className="text-white/40 text-xs">({x.vat_mode === "none" ? "No VAT" : `VAT ${x.vat_rate_percent}%`})</span></span>
              <div className="flex gap-2">
                <button onClick={() => openPdf(x.proposal_id)} data-testid="view-pdf-btn" className="text-white/60 hover:text-cyan-300" title="PDF"><Eye size={16} /></button>
                {x.locked || (x.doc_type === "invoice" && x.biz_flow) ? (
                  <span className="text-[0.68rem] text-white/45" data-testid="proposal-locked-note">{x.locked ? tx("Kilitli · Finans'ta yönet") : tx("Finans'ta yönet")}</span>
                ) : (
                  <>
                    <button onClick={() => { setEditing({ ...x, vat_rate: x.vat_rate ?? x.vat_rate_percent ?? 0 }); }} className="text-white/60 hover:text-cyan-300">{t("edit")}</button>
                    <button onClick={() => del(x.proposal_id)} className="text-white/50 hover:text-red-400"><Trash2 size={15} /></button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Line = ({ l, r }) => <div className="flex justify-between text-white/80"><span>{l}</span><span>£{Number(r).toFixed(2)}</span></div>;
const Sel = ({ label, value, onChange, opts }) => (
  <label className="block mb-3">
    <span className="label-eyebrow block mb-1">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-black/30 border border-cyan-500/20 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-400/60">
      {opts.map((o) => <option key={o[0]} value={o[0]} className="bg-[#0C1222]">{o[1]}</option>)}
    </select>
  </label>
);

export default ProposalsPanel;
